import { useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import {
  API_RESULT_LIMITS,
  SEARCH_FORM_SETTINGS,
} from 'src/constants/settings';
import type {
  ModulePermissionOrgUnit,
  PlanActionPermissionOrgUnit,
} from 'src/types/orgUnit';
import useDebounce from 'src/hooks/useDebounce';
import { apiPaths } from 'src/services/api/apiPaths';
import http from 'src/services/api/http';
import { useSection } from 'src/components/app/context/SectionContext';

import useOrgTree from '../OrgTreeContext';

export type UseSearchResults = {
  (searchProps: { searchTerm: string }): {
    loading: boolean;
    orgUnits: ModulePermissionOrgUnit[] | PlanActionPermissionOrgUnit[] | null;
  };
};

const useSearchResults: UseSearchResults = ({ searchTerm }) => {
  const [orgUnits, setOrgUnits] = useState(null);
  const [loading, setLoading] = useState(false);
  const { surveyId, moduleId: moduleIdFromOrgTree } = useOrgTree();
  const { moduleId: moduleIdFromSection } = useSection();

  const moduleId = useMemo(
    () => moduleIdFromOrgTree ?? moduleIdFromSection,
    [moduleIdFromOrgTree, moduleIdFromSection],
  );

  const debouncedSearchTerm = useDebounce(
    searchTerm,
    SEARCH_FORM_SETTINGS.DEBOUNCE_TIME,
  );

  useEffect(() => {
    setOrgUnits(null);

    if (debouncedSearchTerm && surveyId !== undefined) {
      setLoading(true);

      const endpoint = `${generatePath(apiPaths.ORG_UNIT_BY_SURVEY, {
        surveyId,
      })}?module_id=${moduleId}&search=${debouncedSearchTerm}&limit=${API_RESULT_LIMITS.SEARCH}`;

      http
        .get(endpoint)
        .then((response: any) => setOrgUnits(response.data.results))
        .catch(() => undefined) // An error can still be shown via <ErrorHandler /> up in the tree
        .finally(() => setLoading(false));
    }
  }, [debouncedSearchTerm, moduleId, surveyId]);

  return { loading, orgUnits };
};

export default useSearchResults;
